/**
 * Custom attributes table for the Members Custom Attributes settings screen.
 *
 * Mirrors the departments table: renders the React Table plus a "last row" that adapts
 * to state (loading spinner, empty state with a prominent add button, or a compact
 * "Add attribute" row when attributes already exist).
 */
import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus, LoaderIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';

import { CustomAttributeRowFormData } from '../../validations/custom-attribute-validation';

interface CustomAttributesTableProps {
  /** Configured React Table instance with attribute rows and columns */
  table: Table<CustomAttributeRowFormData>;
  /** Callback when the add attribute button is clicked */
  onAddAttribute: () => void;
  /** Whether the table currently has any attributes */
  hasAttributes: boolean;
  /** Whether data is currently being loaded */
  isLoading?: boolean;
  /** Unique table ID enabling column resizing + persisted widths */
  tableId?: string;
  /** Called when a column resize gesture ends, to persist the new widths */
  onResizeEnd?: () => void;
}

const I18N = 'pages.membersCustomAttributes';

export const CustomAttributesTable: React.FC<CustomAttributesTableProps> = ({
  table,
  onAddAttribute,
  hasAttributes,
  isLoading = false,
  tableId,
  onResizeEnd,
}) => {
  const { t } = useTranslation('authenticated.settings');

  const renderLoadingState = (): React.ReactNode => {
    if (!isLoading) return null;
    return (
      <tr>
        <td colSpan={table.getAllColumns().length} className="h-32 px-0">
          <StickyTableStateContent align="center" className="px-4 py-3">
            <LoaderIcon className="h-4 w-4 animate-spin text-foreground opacity-50" />
          </StickyTableStateContent>
        </td>
      </tr>
    );
  };

  const renderEmptyState = (): React.ReactNode => {
    if (hasAttributes || isLoading) return null;
    return (
      <tr>
        <td colSpan={table.getAllColumns().length} className="px-0 py-4">
          <StickyTableStateContent className="items-start gap-3 px-8 text-left">
            <div className="flex flex-col items-start gap-0.5">
              <div className="text-sm font-semibold text-foreground">
                {t(`${I18N}.emptyState.title`)}
              </div>
              <div className="text-xs text-muted-foreground">
                {t(`${I18N}.emptyState.description`)}
              </div>
            </div>
            <Button type="button" onClick={onAddAttribute} className="h-10 gap-2">
              <CirclePlus className="h-4 w-4" />
              {t(`${I18N}.addAttribute`)}
            </Button>
          </StickyTableStateContent>
        </td>
      </tr>
    );
  };

  const renderAddAttributeRow = (): React.ReactNode => {
    if (!hasAttributes) return null;
    return (
      <tr className="hover:bg-transparent">
        <td colSpan={table.getAllColumns().length} className="h-16 px-4">
          <div className="flex justify-start">
            <Button
              type="button"
              variant="ghost"
              onClick={onAddAttribute}
              className="gap-2"
              size="sm"
            >
              <CirclePlus className="h-4 w-4" />
              {t(`${I18N}.addAttribute`)}
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  const lastRowChildren = (
    <>
      {renderLoadingState()}
      {renderEmptyState()}
      {renderAddAttributeRow()}
    </>
  );

  return (
    <DataTable
      table={table}
      lastRowChildren={lastRowChildren}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
    />
  );
};
